/**
 * Google Ads (AW) tag + lead-form conversion reporting.
 *
 * gtag.js is loaded once in the root layout (currently with the GA4 id).
 * Loading it with any product id registers a single global `window.gtag`
 * that serves every Google product, so the Ads tag only needs an extra
 * `gtag('config', ...)` call in the layout, and conversions just call
 * `window.gtag` from anywhere. These helpers keep the ids in one place and
 * make the conversion call a safe no-op when the tag hasn't loaded yet
 * (server render, slow network, ad-blockers).
 */

// Public by design — Google publishes these in every page's source, so a
// hardcoded fallback is fine. The env override keeps staging/preview
// deploys from firing conversions into the live Ads account.
export const GOOGLE_ADS_ID =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_ID || "AW-18142701949";

// "send_to" target for the Submit-lead-form conversion action.
export const LEAD_FORM_CONVERSION_SEND_TO =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_LEAD_LABEL ||
  "AW-18142701949/Bok0COLr8KgcEP3SjstD";

type GtagFn = (...args: unknown[]) => void;

function getGtag(): GtagFn | null {
  if (typeof window === "undefined") return null;
  const g = (window as unknown as { gtag?: GtagFn }).gtag;
  return typeof g === "function" ? g : null;
}

/**
 * Fire the Google Ads "Submit lead form" conversion. Call ONLY after a
 * lead form has actually been submitted successfully — never on page load,
 * or every visit would be counted as a conversion.
 */
export function reportLeadFormConversion(value = 1.0, currency = "SGD"): void {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", "conversion", {
    send_to: LEAD_FORM_CONVERSION_SEND_TO,
    value,
    currency,
  });
}
